// Spec 084 — a parte do contrato da importação que fala da BATELADA.
//
// Arquivo próprio pelo teto de 200 linhas efetivas, e a divisão não é só de tamanho: o resto de
// `data-import.ts` descreve a coluna que vira QUANTIDADE; aqui está a coluna que descreve o
// MATERIAL, o de-para das grafias e a ligação entre etapas.
import { z } from "zod";
import { UuidSchema } from "./common.js";
import { BatchCharacteristicKindSchema } from "./batch-characteristics.js";
import type { ImportColumnMapping } from "./data-import.js";

/**
 * Spec 084 — o DE-PARA de uma grafia do arquivo.
 *
 * A chave do dicionário é o texto CRU e exato da célula, e não uma forma normalizada: "Tipo A" e
 * "tipo a" podem ser a mesma coisa nesta fábrica e coisas diferentes na próxima. Quem decide é o
 * gestor, uma vez, e o perfil guarda a decisão (FR-021).
 *
 * `notInformed` existe porque "esta grafia não é um valor" é uma resposta legítima — e diferente de
 * "ainda não decidi", que é a ausência da chave e bloqueia a validação.
 */
export const ValueTranslationSchema = z.union([
  z.object({ valueId: UuidSchema }).strict(),
  z.object({ notInformed: z.literal(true) }).strict(),
]);
export type ValueTranslation = z.infer<typeof ValueTranslationSchema>;

/**
 * Spec 084 — a regra que liga a batelada de uma etapa à da anterior pela MESMA linha do arquivo.
 *
 * É a declaração que vale como a confirmação exigida pelo FR-008a: ao dizer "nesta planilha, a
 * Etapa 2 de cada linha consome o que a Etapa 1 da mesma linha produziu", o gestor está associando
 * origem uma vez para o arquivo inteiro.
 */
export const ImportOriginRuleSchema = z
  .object({ upstreamLineId: UuidSchema, downstreamLineId: UuidSchema })
  .strict()
  .refine((r) => r.upstreamLineId !== r.downstreamLineId, {
    message: "A etapa de origem tem de ser diferente da que recebe.",
  });
export type ImportOriginRule = z.infer<typeof ImportOriginRuleSchema>;

export const ImportOriginRulesSchema = z.array(ImportOriginRuleSchema).max(50).default([]);

/** A mesma característica em duas colunas: qual delas vale? Recusado (contrato). */
export function findDuplicateCharacteristic(
  mapping: Record<string, ImportColumnMapping>
): { characteristicId: string; columns: string[] } | null {
  const seen = new Map<string, string[]>();
  for (const [column, decision] of Object.entries(mapping)) {
    if (decision.action !== "CHARACTERISTIC") continue;
    const columns = seen.get(decision.characteristicId) ?? [];
    columns.push(column);
    seen.set(decision.characteristicId, columns);
  }
  for (const [characteristicId, columns] of seen) {
    if (columns.length > 1) return { characteristicId, columns };
  }
  return null;
}

/**
 * Spec 084 — as GRAFIAS de uma coluna de característica, contadas sobre o ARQUIVO INTEIRO.
 *
 * Sobre o arquivo inteiro, e não sobre a amostra de cem linhas: o gestor decide o de-para uma vez,
 * e uma grafia que só aparece na linha 400 apareceria como surpresa na execução — ou, pior, viraria
 * "não informado" sem ninguém ter escolhido isso.
 */
export const ImportCharacteristicColumnSchema = z.object({
  column: z.string(),
  characteristicId: UuidSchema,
  characteristicName: z.string(),
  kind: BatchCharacteristicKindSchema,
  required: z.boolean(),
  values: z.array(
    z.object({
      /** Exatamente como veio da célula. */
      raw: z.string(),
      rows: z.number().int(),
      /** `null` = pendente de decisão (só acontece em característica de lista). */
      translation: z.union([ValueTranslationSchema, z.null()]),
    })
  ),
  /** Células em branco: viram "não informado", e isso é uma resposta, não uma falta. */
  emptyRows: z.number().int(),
  pendingCount: z.number().int(),
});
export type ImportCharacteristicColumn = z.infer<typeof ImportCharacteristicColumnSchema>;

export const ImportOriginPreviewSchema = z.object({
  upstreamLineName: z.string(),
  downstreamLineName: z.string(),
  associated: z.number().int(),
  /** FR-024d — linhas do arquivo em que a etapa de baixo registra e a de cima não. */
  withoutUpstream: z.number().int(),
});
export type ImportOriginPreview = z.infer<typeof ImportOriginPreviewSchema>;
